import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { domainService } from '../../services/domainService';
import { CustomDomainStatus } from '../../types/domain';
import { DomainStatusBadge } from './DomainStatusBadge';
import { DnsRecordsDisplay } from './DnsRecordsDisplay';
import { Loader2, Globe, Trash2, RefreshCw, Plus } from 'lucide-react';

interface CustomDomainSettingsProps {
  customDomain: string | null;
  isEnabled: boolean;
  onDomainUpdated: () => void;
}

export const CustomDomainSettings: React.FC<CustomDomainSettingsProps> = ({
  customDomain,
  isEnabled,
  onDomainUpdated,
}) => {
  const { toast } = useToast();
  const [newDomain, setNewDomain] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [domainStatus, setDomainStatus] = useState<CustomDomainStatus | null>(null);
  const [showDnsRecords, setShowDnsRecords] = useState(false);

  const validateDomainFormat = (domain: string): boolean => {
    const domainPattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;
    return domainPattern.test(domain.toLowerCase());
  };

  const handleAddDomain = async () => {
    if (!newDomain || !validateDomainFormat(newDomain)) {
      toast({
        title: 'Invalid Domain',
        description: 'Please enter a valid domain name (e.g., crm.acme.com)',
        variant: 'destructive',
      });
      return;
    }

    setIsAdding(true);
    try {
      const status = await domainService.addCustomDomain(newDomain);
      setDomainStatus(status);
      setShowDnsRecords(true);
      setNewDomain('');
      toast({
        title: 'Domain Added!',
        description: 'Please configure DNS records to verify your domain',
      });
      onDomainUpdated();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add custom domain',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleVerifyDomain = async () => {
    setIsVerifying(true);
    try {
      const status = await domainService.verifyCustomDomain();
      setDomainStatus(status);
      setShowDnsRecords(true);

      if (status.isValidated) {
        toast({
          title: 'Domain Verified!',
          description: 'Your custom domain is now active',
        });
        onDomainUpdated();
      } else {
        toast({
          title: 'Verification Pending',
          description: status.error || 'DNS records not yet propagated. Please try again in a few minutes.',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to verify domain',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!confirm('Are you sure you want to remove this custom domain? Your workspace will only be accessible via the subdomain.')) {
      return;
    }

    setIsRemoving(true);
    try {
      await domainService.removeCustomDomain();
      setDomainStatus(null);
      setShowDnsRecords(false);
      toast({
        title: 'Domain Removed',
        description: 'Custom domain has been removed',
      });
      onDomainUpdated();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove domain',
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleShowDnsRecords = async () => {
    if (!customDomain) return;

    setIsVerifying(true);
    try {
      const status = await domainService.verifyCustomDomain();
      setDomainStatus(status);
      setShowDnsRecords(true);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch DNS records',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // No custom domain configured
  if (!customDomain) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom Domain</CardTitle>
          <CardDescription>
            Use your own domain name instead of a subdomain (e.g., crm.yourcompany.com)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Domain Name</label>
            <div className="flex gap-2">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value.toLowerCase().trim())}
                placeholder="crm.yourcompany.com"
                disabled={isAdding}
              />
              <Button onClick={handleAddDomain} disabled={isAdding || !newDomain}>
                {isAdding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded border border-blue-200 dark:border-blue-800">
            <p className="font-medium mb-2">💡 What you'll need:</p>
            <ul className="list-disc list-inside space-y-1 mb-3">
              <li>Access to your domain's DNS settings (GoDaddy, Namecheap, Cloudflare, etc.)</li>
              <li>Ability to add CNAME records</li>
              <li>5-10 minutes for DNS propagation after adding records</li>
            </ul>
            
            <p className="font-medium mb-2">📋 DNS Record Example:</p>
            <div className="bg-white dark:bg-gray-900 p-2 rounded border font-mono text-[10px]">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <span className="text-muted-foreground">Type:</span>
                  <div className="font-semibold">CNAME</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <div className="font-semibold">crm.yourcompany.com</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Value:</span>
                  <div className="font-semibold truncate">proxy.cloudflare.com</div>
                </div>
              </div>
            </div>
            <p className="mt-2 text-[10px] italic">
              * The actual CNAME value will be provided after adding your domain
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Custom domain configured
  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Domain</CardTitle>
        <CardDescription>
          Your custom domain configuration and status
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Domain Display */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded">
              <Globe className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">{customDomain}</p>
              <p className="text-xs text-muted-foreground">Custom Domain</p>
            </div>
          </div>
          <DomainStatusBadge
            status={
              isEnabled ? 'active' : domainStatus?.status === 'error' ? 'error' : 'pending'
            }
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {!isEnabled && (
            <Button
              onClick={handleVerifyDomain}
              disabled={isVerifying}
              className="flex-1"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Verify Domain
                </>
              )}
            </Button>
          )}
          
          {!showDnsRecords && (
            <Button
              onClick={handleShowDnsRecords}
              variant="outline"
              disabled={isVerifying}
            >
              Show DNS Records
            </Button>
          )}

          <Button
            onClick={handleRemoveDomain}
            variant="destructive"
            disabled={isRemoving}
          >
            {isRemoving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </>
            )}
          </Button>
        </div>

        {/* DNS Records Display */}
        {showDnsRecords && domainStatus && (
          <DnsRecordsDisplay records={domainStatus.dnsRecords} />
        )}

        {/* Error Message */}
        {domainStatus?.error && (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 p-3 rounded border border-red-200 dark:border-red-800">
            <p className="font-medium mb-1">⚠️ Configuration Error:</p>
            <p>{domainStatus.error}</p>
          </div>
        )}

        {/* Success Message */}
        {isEnabled && (
          <div className="text-sm text-green-600 bg-green-50 dark:bg-green-950 p-3 rounded border border-green-200 dark:border-green-800">
            <p className="font-medium mb-1">✅ Domain Active!</p>
            <p>Your custom domain is configured and working. Users can access your workspace at {customDomain}</p>
          </div>
        )}

        {/* Pending Message */}
        {!isEnabled && !domainStatus?.error && (
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded">
            <p className="font-medium mb-1">⏳ Verification Pending:</p>
            <p>
              Configure the DNS records above with your domain provider, then click "Verify Domain".
              DNS changes typically take 5-10 minutes to propagate.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
